import React, { useRef } from 'react';
import {
  Breakpoint,
  isBreakpointLargeOrAbove,
  useBreakpoint,
} from '@noths/polaris-client-ribbons-base';
import { Media } from '@noths/polaris-client-ribbons-design-system';
import { Image } from '@noths/polaris-dev-ts-types';

import { Modal, styles as modalStyles } from 'src/components/molecules/Modal';
import { Product } from 'src/types';
import { getIconPath } from 'src/utils';
import { ThumbnailsContainer } from '../../containers/ThumbnailsContainer';
import {
  ImagesLocation,
  ProductImagesState,
  SetCurrentImagePayload,
  ThumbnailOrientation,
} from '../../types';
import { MultipleProductImages } from '../MultipleProductImages';
import { ProductImagesModal } from './ProductImagesModal';
import * as styles from '../../styles/ProductImageModal.styles';
import * as productImagesStyles from '../../styles/ProductImages.styles';

export const IMAGES_ID_PREFIX = 'fullscreen-';

const ICON_CLOSE = getIconPath('icons-24-close.svg');

export interface ProductImageModalProps {
  product: Product;
  images: Image[];
  currentImage: ProductImagesState['currentImage'];
  isModalOpen: ProductImagesState['isModalOpen'];
  closeModal: VoidFunction;
  setCurrentImage: (payload: SetCurrentImagePayload) => void;
}

export const ProductImageModal = ({
  product,
  isModalOpen,
  closeModal,
  images,
  currentImage,
  setCurrentImage,
}: ProductImageModalProps) => {
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [loadedImages, setImageLoaded] = React.useState<Record<number, boolean>>({});
  const [navigatedBy, setNavigatedBy] = React.useState<ProductImagesState['navigatedBy']>('swipe');
  const breakpoint = useBreakpoint();
  const orientation = isBreakpointLargeOrAbove(breakpoint)
    ? ThumbnailOrientation.VERTICAL
    : ThumbnailOrientation.HORIZONTAL;

  const multipleImagesToDisplay = images.length > 1;
  const location: ImagesLocation = 'Expanded image';

  return (
    <>
      <Modal
        isModalOpen={isModalOpen}
        closeModal={() => closeModal()}
        contentLabel="View full screen image"
      >
        <div css={[modalStyles.modalViewport, styles.modalContentContainer]} ref={innerRef}>
          <div css={styles.fullViewContainer}>
            {multipleImagesToDisplay && (
              <div css={styles.thumbnails}>
                <ThumbnailsContainer
                  orientation={orientation}
                  onThumbnailClick={(imageIndex) => {
                    setNavigatedBy('click');
                    setCurrentImage({
                      imageIndex: imageIndex,
                      navigationType: 'click',
                      location,
                    });
                  }}
                  idPrefix={IMAGES_ID_PREFIX}
                  location={location}
                />
              </div>
            )}
            <div css={styles.imagesContainer}>
              <Media lessThan={Breakpoint.M}>
                {(className) => {
                  return (
                    <span className={className}>
                      {multipleImagesToDisplay ? (
                        <MultipleProductImages
                          location={location}
                          setCurrentImage={setCurrentImage}
                          product={product}
                          navigatedBy={navigatedBy}
                          currentImage={currentImage}
                          onBeforeSwipe={() => setNavigatedBy('swipe')}
                          onNextImage={() => {}}
                          onPrevImage={() => {}}
                          onOpenModal={() => {}}
                        />
                      ) : (
                        <ProductImagesModal
                          images={images}
                          currentImage={currentImage}
                          multipleImagesToDisplay={multipleImagesToDisplay}
                          loadedImages={loadedImages}
                          setImageLoaded={setImageLoaded}
                        />
                      )}
                    </span>
                  );
                }}
              </Media>
              <Media greaterThanOrEqual={Breakpoint.M}>
                {(className) => (
                  <span className={className}>
                    <ProductImagesModal
                      images={images}
                      currentImage={currentImage}
                      multipleImagesToDisplay={multipleImagesToDisplay}
                      loadedImages={loadedImages}
                      setImageLoaded={setImageLoaded}
                    />
                    {multipleImagesToDisplay && (
                      <p css={productImagesStyles.progressIndicator} aria-hidden="true">
                        {`${currentImage + 1} of ${images.length}`}
                      </p>
                    )}
                  </span>
                )}
              </Media>
            </div>
          </div>
          <button css={styles.modalCloseButton} onClick={() => closeModal()}>
            <img src={ICON_CLOSE} alt="Close" width={24} height={24} />
          </button>
        </div>
      </Modal>
    </>
  );
};
